// =============================================================================
// hestia/drapeauHestia.js : le drapeau d'Hestia, seul morceau du bundle principal.
// -----------------------------------------------------------------------------
// useDrapeauHestia(sessionId) → { habilite, assistanteSeule, aTraiter } :
//   • chargé APRÈS le premier rendu (la couche d'accès arrive par import(),
//     jamais dans le bundle principal), jamais bloquant ;
//   • mémorisé pour l'onglet (sessionStorage, par identifiant de session) :
//     le rail ne clignote pas d'une page à l'autre ;
//   • échec fermé : sans réponse, Hestia reste hors du rail (la vue reste de
//     toute façon gardée côté serveur) ;
//   • relu quand une action a eu lieu, ou quand le rôle fictif change dans la
//     maquette du CODIR (acces.abonner).
// Sert le rail (buildNavModules, isHabiliteHestia), le fil de l'en-tête et la
// ligne « À faire » de Ma semaine (aTraiter : présence seulement, aucun nombre).
// Hestia sert l'identité du JETON : on passe la session RÉELLE, jamais celle
// de « Voir comme ».
// =============================================================================
import { useEffect, useState } from "react";

export const DRAPEAU_VIDE = Object.freeze({ habilite: false, assistanteSeule: false, aTraiter: [] });
const CLE = "hestia_drapeau";
const DELAI_MS = 1500;

function lireMemoire(sessionId) {
  if (!sessionId) return DRAPEAU_VIDE;
  try {
    const m = JSON.parse(sessionStorage.getItem(CLE) || "null");
    return m && m.sessionId === sessionId && m.drapeau ? m.drapeau : DRAPEAU_VIDE;
  } catch { return DRAPEAU_VIDE; }
}

/** Ne garde que les champs attendus (une mémoire altérée n'ouvre rien de plus). */
export function drapeauPropre(d) {
  if (!d || typeof d !== "object") return DRAPEAU_VIDE;
  return {
    habilite: d.habilite === true,
    assistanteSeule: d.assistanteSeule === true,
    aTraiter: (Array.isArray(d.aTraiter) ? d.aTraiter : [])
      .filter((l) => l && typeof l.cle === "string" && typeof l.libelle === "string")
      .map((l) => ({ cle: l.cle, libelle: l.libelle })),
  };
}

export function useDrapeauHestia(sessionId) {
  const [drapeau, setDrapeau] = useState(() => lireMemoire(sessionId));
  useEffect(() => {
    if (!sessionId) { setDrapeau(DRAPEAU_VIDE); return undefined; }
    setDrapeau(lireMemoire(sessionId));
    let vivant = true;
    let desabonner = null;
    const charger = () => import("./acces")
      .then((acces) => {
        if (!desabonner && vivant) desabonner = acces.abonner(charger);
        return acces.drapeau();
      })
      .then((d) => {
        if (!vivant) return;
        const propre = drapeauPropre(d);
        setDrapeau(propre);
        try { sessionStorage.setItem(CLE, JSON.stringify({ sessionId, drapeau: propre })); } catch { /* onglet sans stockage */ }
      })
      .catch(() => {});
    const t = setTimeout(charger, DELAI_MS);
    return () => { vivant = false; clearTimeout(t); if (desabonner) desabonner(); };
  }, [sessionId]);
  return drapeau;
}
